import { useEffect, useMemo, useRef } from 'react';
import type {
  CalendarCloseEvent,
  CalendarOpenEvent,
  CustomPresetClickEvent,
  DateChangedEvent,
  InputClearedEvent,
  InvalidDateEvent,
  NavigationEvent,
  ReadyEvent,
  SgDateRangePickerHTMLElement,
  SgDateRangePickerProperties,
} from './types';
import { startOfDay } from 'date-fns/startOfDay';
import type { Size } from '../layout';
import { isDefined, isNotDefined } from '@/utils/assertions';

export type SgDateRangePickerProps = {
  className?: string;
  dataE2e?: string;
  disabled?: boolean;
  endValue?: EpochTimeStamp;
  locale?: 'en' | 'fr';
  noClearButton?: boolean;
  onCalendarOpened?: (event: CalendarOpenEvent) => void;
  onCalendarclosed?: (event: CalendarCloseEvent) => void;
  onChange?: (event: DateChangedEvent) => void;
  onInputCleared?: (event: InputClearedEvent) => void;
  onInputInvalid?: (event: InvalidDateEvent) => void;
  placement?: 'bottom' | 'top';
  readonly?: boolean;
  size?: Size;
  startValue?: EpochTimeStamp;
  validationMessage?: string;
  validationState?: SgDateRangePickerProperties['state'];
  yearControllersDisabled?: boolean;
  maxValue?: Date | string;
  minValue?: Date | string;
};

/**
 * https://developer.sgmarkets.com/docs/design-system/v5/components/daterange-pickers.html
 */
export const SgDateRangePicker = (props: SgDateRangePickerProps) => {
  const {
    className,
    dataE2e,
    disabled,
    endValue,
    locale,
    noClearButton,
    onCalendarOpened,
    onCalendarclosed,
    onChange,
    onInputCleared,
    onInputInvalid,
    placement = 'bottom',
    readonly,
    size,
    startValue,
    validationState,
    validationMessage,
    yearControllersDisabled,
    maxValue,
    minValue,
  } = props;

  const innerRef = useRef<SgDateRangePickerHTMLElement>(null);


  const disablePresets = useMemo(() => {
    const rules: string[] = [];
    if (minValue) rules.push(`before:${startOfDay(minValue).getTime()}`);
    if (maxValue) rules.push(`after:${startOfDay(maxValue).getTime()}`);
    return rules.join(', ');
  }, [minValue, maxValue]);

  const onCalendarCloseEvent = (event: CalendarCloseEvent) => {
    if (isNotDefined(onCalendarclosed)) {
      return;
    }

    return onCalendarclosed(event);
  };

  const onCalendarOpenEvent = (event: CalendarOpenEvent) => {
    if (isNotDefined(onCalendarOpened)) {
      return;
    }

    return onCalendarOpened(event);
  };

  const onCustomPresetClickEvent = (_event: CustomPresetClickEvent) => { };

  const onDateChangedEvent = (event: DateChangedEvent) => {
    if (isNotDefined(onChange)) {
      return;
    }

    onChange(event);
  };

  const onInputClearedEvent = (event: InputClearedEvent) => {
    if (isNotDefined(onInputCleared)) {
      return;
    }

    onInputCleared(event);
  };

  const onInvalidDateEvent = (event: InvalidDateEvent) => {
    if (isNotDefined(onInputInvalid)) {
      return;
    }

    onInputInvalid(event);
  };

  const onNavigationEvent = (_event: NavigationEvent) => { };

  const onReadyEvent = (_event: ReadyEvent) => { };

  // The sg-daterangepicker web component resets its own validation state whenever
  // the user picks a new date. React won't re-write the `state`/`validation-message`
  // attributes when their values are unchanged (e.g. two successive out-of-range
  // selections produce the same `invalid` state and message), so the error would
  // disappear. Re-apply the validation imperatively on every value/validation change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-apply must also run when the selected dates change
  useEffect(() => {
    const element = innerRef.current;
    if (isNotDefined(element)) {
      return;
    }
    if (isDefined(validationState)) {
      element.setState?.(validationState, validationMessage ?? '');
    } else {
      element.resetState?.();
    }
  }, [validationState, validationMessage, startValue, endValue]);

  useEffect(() => {
    if (isNotDefined(innerRef.current)) {
      return;
    }

    innerRef.current.addEventListener('calendarClose', onCalendarCloseEvent as EventListener);
    innerRef.current.addEventListener('calendarOpen', onCalendarOpenEvent as EventListener);
    innerRef.current.addEventListener(
      'customPresetClickEvent',
      onCustomPresetClickEvent as EventListener,
    );
    innerRef.current.addEventListener('dateChanged', onDateChangedEvent as EventListener);
    innerRef.current.addEventListener('inputCleared', onInputClearedEvent as EventListener);
    innerRef.current.addEventListener('invalidDate', onInvalidDateEvent as EventListener);
    innerRef.current.addEventListener('navigation', onNavigationEvent as EventListener);
    innerRef.current.addEventListener('ready', onReadyEvent as EventListener);

    return () => {
      if (isNotDefined(innerRef.current)) {
        return;
      }

      innerRef.current.removeEventListener(
        'calendarCloseEvent',
        onCalendarCloseEvent as EventListener,
      );
      innerRef.current.removeEventListener(
        'calendarOpenEvent',
        onCalendarOpenEvent as EventListener,
      );
      innerRef.current.removeEventListener(
        'customPresetClickEvent',
        onCustomPresetClickEvent as EventListener,
      );
      innerRef.current.removeEventListener('dateChangedEvent', onDateChangedEvent as EventListener);
      innerRef.current.removeEventListener(
        'inputClearedEvent',
        onInputClearedEvent as EventListener,
      );
      innerRef.current.removeEventListener('invalidDateEvent', onInvalidDateEvent as EventListener);
      innerRef.current.removeEventListener('navigationEvent', onNavigationEvent as EventListener);
      innerRef.current.removeEventListener('readyEvent', onReadyEvent as EventListener);
    };
  });

  // why min-width: 330 px?
  // because it is the minimum size to display all the end date when the reset button is visible (by default)
  return (
    <sg-daterangepicker
      ref={innerRef}
      append-to="body"
      className={className}
      data-e2e={dataE2e}
      disabled={disabled}
      end-date={endValue}
      locale={locale === 'fr' ? 'fr-FR' : 'en-GB'}
      no-clear-button={noClearButton}
      readonly={readonly}
      size={size}
      start-date={startValue}
      state={validationState}
      top={placement === 'top'}
      validation-message={validationMessage}
      year-controllers={yearControllersDisabled !== true}
      disable-presets={disablePresets}
    />
  );
};
